import { useUser } from '@clerk/clerk-react';
import {
	faArrowsRotate,
	faBuildingCircleExclamation,
	faClock,
	faEnvelope,
	faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import {
	createFileRoute,
	type ErrorComponentProps,
	redirect,
} from '@tanstack/react-router';
import { getMetaDataQueryOptions, UserAssociationStatus } from '@uturn/api/v1';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import { Button, Icon } from '@uturn/ui-kit';
import { useTranslation } from 'react-i18next';
import { ErrorFallback } from '../../components/error-fallback';
import AppLayout from '../../layout';
import { logErrorToSentry } from '../../lib/log-error-to-sentry';

const SUPPORT_EMAIL_ADDRESS = 'customerservice@uturn-now.com';
const NO_ORGANISATION_ERROR = 'NO_ORGANISATION';
const PENDING_ORGANISATION_ERROR = 'PENDING_ORGANISATION';

const ProtectedErrorComponent = ({ error }: ErrorComponentProps) => {
	const { t } = useTranslation();
	const { user } = useUser();
	const { logout } = useAuth();

	const handleLogout = async () => {
		await logout();
		window.location.href = '/sign-in';
	};

	const headerUser = user
		? {
				name: user.fullName ?? 'User',
				role: (user.publicMetadata?.role as string) ?? '',
			}
		: null;

	if (error instanceof Error && error.message === PENDING_ORGANISATION_ERROR) {
		const supportSubject = t(
			'errors.pending-organisation.support-subject',
			'My organisation is awaiting approval',
		);

		return (
			<ErrorFallback
				icon={faClock}
				title={t(
					'errors.pending-organisation.title',
					'Your organisation is awaiting approval',
				)}
				message={t(
					'errors.pending-organisation.message',
					'Your organisation is pending approval. One of our team members will contact you shortly to get you started.',
				)}
				actions={
					<div className="space-y-3">
						<Button
							onClick={() => window.location.reload()}
							className="w-full"
							size="lg"
						>
							<Icon className="mr-2 h-4 w-4" icon={faArrowsRotate} />
							{t('errors.reload-page', 'Reload page')}
						</Button>
						<div className="flex gap-2">
							<Button asChild className="flex-1" size="lg" variant="ghost">
								<a
									href={`mailto:${SUPPORT_EMAIL_ADDRESS}?subject=${encodeURIComponent(supportSubject)}`}
								>
									<Icon className="mr-2 h-4 w-4" icon={faEnvelope} />
									{t(
										'errors.pending-organisation.contact-support',
										'Contact support',
									)}
								</a>
							</Button>
							<Button
								onClick={handleLogout}
								className="flex-1"
								size="lg"
								variant="ghost"
							>
								{t('header.navigation.user.log-out', 'Log out')}
							</Button>
						</div>
					</div>
				}
				onLogout={handleLogout}
				user={headerUser}
			/>
		);
	}

	if (error instanceof Error && error.message === NO_ORGANISATION_ERROR) {
		const supportSubject = t(
			'errors.no-organisation.support-subject',
			'No organisation linked to my account',
		);

		return (
			<ErrorFallback
				icon={faBuildingCircleExclamation}
				title={t(
					'errors.no-organisation.title',
					'Your account is not linked to an organisation',
				)}
				message={t(
					'errors.no-organisation.message',
					"We can't show your shipments because your account is not linked to an organisation yet. Contact our support team and we'll get you set up.",
				)}
				actions={
					<div className="space-y-3">
						<Button asChild className="w-full" size="lg">
							<a
								href={`mailto:${SUPPORT_EMAIL_ADDRESS}?subject=${encodeURIComponent(supportSubject)}`}
							>
								<Icon className="mr-2 h-4 w-4" icon={faEnvelope} />
								{t('errors.no-organisation.contact-support', 'Contact support')}
							</a>
						</Button>
						<div className="flex gap-2">
							<Button
								onClick={() => window.location.reload()}
								className="flex-1"
								size="lg"
								variant="ghost"
							>
								<Icon className="mr-2 h-4 w-4" icon={faArrowsRotate} />
								{t('errors.reload-page', 'Reload page')}
							</Button>
							<Button
								onClick={handleLogout}
								className="flex-1"
								size="lg"
								variant="ghost"
							>
								{t('header.navigation.user.log-out', 'Log out')}
							</Button>
						</div>
					</div>
				}
				onLogout={handleLogout}
				user={headerUser}
			/>
		);
	}

	// Log to Sentry with XID. Reporting the same error twice returns the same XID
	// and captures only once, so calling this on re-render is safe.
	const xid = logErrorToSentry({
		error,
		context: 'ProtectedRouteError',
		level: 'error',
	});

	return (
		<ErrorFallback
			xid={xid}
			icon={faTriangleExclamation}
			title={t('errors.generic.title', 'Something went wrong')}
			message={t(
				'errors.generic.message',
				"We've encountered an unexpected error. Our team has been notified and will investigate the issue.",
			)}
			errorReferenceLabel={t('errors.error-reference', 'Error Reference')}
			errorReferenceDescription={t(
				'errors.provide-code-when-reporting',
				'Please provide this code when reporting the issue',
			)}
			onLogout={handleLogout}
			user={headerUser}
		/>
	);
};

export const Route = createFileRoute('/_protected')({
	beforeLoad: async ({ context, location }) => {
		if (!context.auth.isAuthenticated) {
			throw redirect({
				to: '/sign-in',
				search: {
					// Use the current location to power a redirect after login
					// (Do not use `router.state.resolvedLocation` as it can
					// potentially lag behind the actual current location)
					redirect: location.href,
				},
			});
		}
		const metaData = await context.queryClient.ensureQueryData(
			getMetaDataQueryOptions(),
		);

		// Gate on whether the user has a confirmed association with the given Organisation.
		const associationStatus = metaData.data.user?.associationStatus;

		if (associationStatus !== UserAssociationStatus.CONFIRMED) {
			throw new Error(
				associationStatus === UserAssociationStatus.PENDING
					? PENDING_ORGANISATION_ERROR
					: NO_ORGANISATION_ERROR,
			);
		}
	},
	loader: async ({ context }) => {
		await Promise.all([
			context.queryClient.ensureQueryData(getMetaDataQueryOptions()),
			context.queryClient.ensureQueryData({
				queryKey: ['shipmentUnits'],
				queryFn: async () => {
					const response = await fetch('/shipment-units.json');
					return response.json();
				},
			}),
		]);
	},
	component: AppLayout,
	errorComponent: ProtectedErrorComponent,
});
